/**
 * dashboardExitGuard — a tiny module-singleton registry that lets a component OUTSIDE
 * the CustomDashboard tree (today: FacilityDropdown, which fires window.location.assign
 * directly) await the active edit session's exit coordinator BEFORE it tears the page
 * down. Only one CustomDashboard is ever mounted, so a single slot is enough.
 *
 * The registered guard is an async function returning a boolean: `true` = safe to leave
 * (layout drained clean, or the user resolved a blocked save via Retry/Discard), `false`
 * = the user chose to keep editing, so the caller must NOT navigate.
 *
 * Mirrors the module-singleton pattern already used by useCrossViewerSync (module-level
 * state + exported functions), not a Vuex module — a function is not serializable state.
 */

import { warn as debugWarn } from '@/utils/debugLogger';

let _guard = null;

/**
 * Register the active edit session's guard. Pass the SAME function reference to
 * clearDashboardExitGuard on teardown so a stale unmount can't clear a newer session's
 * guard (latest-registered wins).
 * @param {() => Promise<boolean>} fn
 */
export function registerDashboardExitGuard(fn) {
  _guard = fn;
}

/**
 * Clear the guard. No-ops unless `fn` is the currently-registered guard, so a late
 * teardown from an old session can't wipe a guard a newer session just installed.
 * @param {() => Promise<boolean>} fn
 */
export function clearDashboardExitGuard(fn) {
  if (_guard === fn) _guard = null;
}

/** True when an edit session has installed a guard. */
export function hasDashboardExitGuard() {
  return !!_guard;
}

/**
 * Run the active guard. Returns `true` (safe to proceed) when no guard is registered,
 * so callers can await unconditionally. Fails OPEN on a guard error — a broken guard
 * must never trap the user on the current facility.
 * @returns {Promise<boolean>}
 */
export async function runDashboardExitGuard() {
  if (!_guard) return true;
  try {
    return await _guard();
  } catch (err) {
    debugWarn('[dashboardExitGuard] guard threw; failing open so navigation is not trapped', err);
    return true;
  }
}

/**
 * Run the active exit guard for navigation call sites that leave the current facility or
 * company (they fire a full page reload). Returns `true` when navigation may proceed: no
 * edit session registered a guard, or the guard resolved clean. Returns `false` only when
 * the guard reports the user chose to keep editing.
 * @returns {Promise<boolean>}
 */
export async function guardDashboardExit() {
  if (!hasDashboardExitGuard()) return true;
  return runDashboardExitGuard();
}

export default {
  registerDashboardExitGuard,
  clearDashboardExitGuard,
  hasDashboardExitGuard,
  runDashboardExitGuard,
  guardDashboardExit,
};
